/**
 * DisplayParametersPanel.jsx
 *
 * UI panel for adjusting display parameters of the embedding visualization.
 * Provides dropdowns and sliders for coloring, point style, path display, label visibility, helper toggles, and size adjustments.
 *
 * Author: [Your Name]
 * Date: [Update as needed]
 */

import React from 'react';
import { PointStyle } from '../objects/CustomPointHelper';
import { DisplayParamTokenColorMode, DisplayParamEnvironmentColorMode, DisplayParamFogColorMode, DisplayParamLabelColorMode } from '../constants';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import IconButton from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Slider from '@mui/material/Slider';

/**
 * DisplayParametersPanel
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.displayParamsOpen - Whether the panel is expanded.
 * @param {function} props.setDisplayParamsOpen - Toggle panel open/close.
 * @param {string} props.displayParam_tokenColorMode - Current coloring mode for display parameters.
 * @param {function} props.onChangeDisplayParam_tokenColorMode - Handler for display parameter coloring mode change.
 * @param {string} props.displayParam_environmentColorMode - Current environment (background) color mode.
 * @param {function} props.onChangeDisplayParam_environmentColorMode - Handler for environment color mode change.
 * @param {string} props.displayParam_fogColorMode - Current fog color mode.
 * @param {function} props.onChangeDisplayParam_fogColorMode - Handler for fog color mode change.
 * @param {boolean} props.showLabels - Whether to show labels.
 * @param {function} props.onToggleShowLabels - Handler for toggling labels.
 * @param {boolean} props.showHelpers - Whether to show 3D helpers.
 * @param {function} props.onToggleShowHelpers - Handler for toggling helpers.
 * @param {number} props.textSize - Size of text labels.
 * @param {function} props.onTextSizeChange - Handler for text size change.
 * @param {number} props.dotSize - Size of points.
 * @param {function} props.onDotSizeChange - Handler for point size change.
 * @param {number} props.labelOffset - Offset for label position.
 * @param {function} props.onLabelOffsetChange - Handler for label offset change.
 * @param {string} props.pointStyle - Current point style.
 * @param {function} props.onChangePointStyle - Handler for point style change.
 * @param {string} props.showPathMode - Current path display mode.
 * @param {function} props.onChangeShowPathMode - Handler for path mode change.
 * @returns {JSX.Element}
 */
export default function DisplayParametersPanel({
  displayParamsOpen,
  setDisplayParamsOpen,
  displayParam_tokenColorMode,
  onChangeDisplayParam_tokenColorMode,
  displayParam_labelColorMode,
  onChangeDisplayParam_labelColorMode,
  displayParam_environmentColorMode,
  onChangeDisplayParam_environmentColorMode,
  displayParam_fogColorMode,
  onChangeDisplayParam_fogColorMode,
  showLabels,
  onToggleShowLabels,
  showHelpers,
  onToggleShowHelpers,
  textSize,
  onTextSizeChange,
  dotSize,
  onDotSizeChange,
  labelOffset,
  onLabelOffsetChange,
  pointStyle,
  onChangePointStyle,
  showPathMode,
  onChangeShowPathMode
}) {
  // Ensure pointStyle is always a valid value for the Select
  const allowedPointStyles = [PointStyle.CIRCLE, PointStyle.RING, PointStyle.CLOUD];
  const safePointStyle = allowedPointStyles.includes(pointStyle) ? pointStyle : PointStyle.CIRCLE;

  // Main container for the panel
  // Contains all display parameter controls (dropdowns, toggles, sliders)
  return (
    <Box
      mb={.5}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        border: '1px solid',
        borderColor: 'grey.300',
        backgroundColor: 'rgba(255,255,255,0.85)',
        borderRadius: 2,
        padding: 1.5,
      }}
    >
      {/* Header with title and help icon */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="subtitle1" fontWeight={600}>
          Display Parameters
        </Typography>
        <Box display="flex" alignItems="center">
          <Tooltip
            title="Customize how the embedding visualization is displayed, including coloring, labels, curve, and 3D helpers."
            placement="top"
            arrow
          >
            <HelpOutlineIcon fontSize="small" sx={{ color: 'text.secondary', cursor: 'pointer', mr: 1 }} />
          </Tooltip>
          {/* Expand/collapse button */}
          <IconButton
            size="small"
            onClick={() => setDisplayParamsOpen((open) => !open)}
            aria-label={displayParamsOpen ? "Collapse" : "Expand"}
            sx={{
              transform: displayParamsOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 0.2s',
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>
      </Box>
      {/* Collapsible content */}
      <Collapse in={displayParamsOpen}>
        {displayParamsOpen && (
          <Box display="flex" flexDirection="column" gap={0}> 

            {/* Token coloring dropdown */}
            <FormControl variant="standard" size="small" sx={{ mt: 0, minWidth: 100 }}>
              <InputLabel id="coloring-select-label">Token Coloring</InputLabel>
              <Tooltip
                title="Choose how to color the points and lines in the visualization: by token ID, by sequence, or no coloring."
                placement="top"
                arrow
              >
                <Select
                  labelId="coloring-select-label"
                  value={displayParam_tokenColorMode}
                  onChange={(e) => onChangeDisplayParam_tokenColorMode(e.target.value)}
                  label="Coloring"
                >
                  <MenuItem value={DisplayParamTokenColorMode.NONE}>None</MenuItem>
                  <MenuItem value={DisplayParamTokenColorMode.TOKEN_ID}>By Token ID</MenuItem>
                  <MenuItem value={DisplayParamTokenColorMode.SEQUENCE}>By Sequence</MenuItem>
                  <MenuItem value={DisplayParamTokenColorMode.BLACK}>Black</MenuItem>
                  <MenuItem value={DisplayParamTokenColorMode.WHITE}>White</MenuItem>
                  {/* Add more color modes here as needed */}
                </Select>
              </Tooltip>
            </FormControl>

            {/* Label coloring dropdown */}
            <FormControl variant="standard" size="small" sx={{ mt: 0, minWidth: 100 }}>
              <InputLabel id="label-coloring-select-label">Label Coloring</InputLabel>
              <Tooltip
                title="Choose how to color the text labels in the visualization."
                placement="top"
                arrow
              >
                <Select
                  labelId="label-coloring-select-label"
                  value={displayParam_labelColorMode}
                  onChange={(e) => onChangeDisplayParam_labelColorMode(e.target.value)}
                  label="Label Coloring"
                >
                  <MenuItem value={DisplayParamLabelColorMode.NONE}>None</MenuItem>
                  <MenuItem value={DisplayParamLabelColorMode.TOKEN_ID}>By Token ID</MenuItem>
                  <MenuItem value={DisplayParamLabelColorMode.SEQUENCE}>By Sequence</MenuItem>
                  <MenuItem value={DisplayParamLabelColorMode.BLACK}>Black</MenuItem>
                  <MenuItem value={DisplayParamLabelColorMode.WHITE}>White</MenuItem>
                  <MenuItem value={DisplayParamLabelColorMode.DARK_BLUE}>Dark Blue</MenuItem>
                  {/* Add more color modes here as needed */}
                </Select>
              </Tooltip>
            </FormControl>

            {/* Background (environment) color dropdown */}
            <FormControl variant="standard" size="small" sx={{ mt: 0, minWidth: 100 }}>
              <InputLabel id="environment-select-label">Environment Color</InputLabel>
              <Tooltip
                title="Set the background/environment color for the 3D visualization."
                placement="top"
                arrow
              >
                <Select
                  labelId="environment-select-label"
                  value={displayParam_environmentColorMode}
                  onChange={e => onChangeDisplayParam_environmentColorMode(e.target.value)}
                  label="Environment Color"
                >
                  <MenuItem value={DisplayParamEnvironmentColorMode.BLACK}>Black</MenuItem>
                  <MenuItem value={DisplayParamEnvironmentColorMode.WHITE}>White</MenuItem>
                  <MenuItem value={DisplayParamEnvironmentColorMode.DARK_BLUE}>Dark Blue</MenuItem>
                </Select>
              </Tooltip>
            </FormControl>

            {/* Fog color dropdown */}
            <FormControl variant="standard" size="small" sx={{ mt: 0, minWidth: 100 }}>
              <InputLabel id="fog-select-label">Fog Color</InputLabel>
              <Tooltip
                title="Set the fog color for the 3D visualization."
                placement="top"
                arrow
              >
                <Select
                  labelId="fog-select-label"
                  value={displayParam_fogColorMode}
                  onChange={e => onChangeDisplayParam_fogColorMode(e.target.value)}
                  label="Fog Color"
                >
                  <MenuItem value={DisplayParamFogColorMode.NONE}>None</MenuItem>
                  <MenuItem value={DisplayParamFogColorMode.BLACK}>Black</MenuItem>
                  <MenuItem value={DisplayParamFogColorMode.WHITE}>White</MenuItem>
                  <MenuItem value={DisplayParamFogColorMode.DARK_BLUE}>Dark Blue</MenuItem>
                </Select>
              </Tooltip>
            </FormControl>
            {/* Point style dropdown */}
            <FormControl variant="standard" size="small" sx={{ mt: 0, minWidth: 100 }}>
              <InputLabel id="pointstyle-select-label">Point Style</InputLabel>
              <Tooltip
                title="Choose the style of the points: circle, ring, or cloud."
                placement="top"
                arrow
              >
                <Select
                  labelId="pointstyle-select-label"
                  value={safePointStyle}
                  onChange={e => onChangePointStyle(e.target.value)}
                  label="Point Style"
                >
                  <MenuItem value={PointStyle.CIRCLE}>Circle</MenuItem>
                  <MenuItem value={PointStyle.RING}>Ring</MenuItem>
                  <MenuItem value={PointStyle.CLOUD}>Cloud</MenuItem>
                </Select>
              </Tooltip>
            </FormControl>

            {/* Path display dropdown */}
            <FormControl variant="standard" size="small" sx={{ mt: 0, minWidth: 100 }}>
              <InputLabel id="showpath-select-label">Show Path</InputLabel>
              <Tooltip
                title="Choose how to display the path between points: as a curve, straight lines, or none."
                placement="top"
                arrow
              >
                <Select
                  labelId="showpath-select-label"
                  value={showPathMode || 'none'}
                  onChange={e => onChangeShowPathMode(e.target.value)}
                  label="Show Path"
                >
                  <MenuItem value="curve">Curve</MenuItem>
                  <MenuItem value="lines">Lines</MenuItem>
                  <MenuItem value="none">None</MenuItem>
                </Select>
              </Tooltip>
            </FormControl>
            {/* Show labels toggle */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={showLabels}
                  onChange={(e) => onToggleShowLabels(e.target.checked)}
                  size="small"
                />
              }
              label="Show first-word labels"
              sx={{ mb: 0.5 }}
            />
            {/* Show helpers toggle */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={showHelpers}
                  onChange={e => onToggleShowHelpers(e.target.checked)}
                  size="small"
                />
              }
              label="Show 3D helpers (axes/grid)"
              sx={{ mb: 0.5 }}
            />
            {/* Text size slider */}
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <Typography variant="body2" fontWeight={500} mb={0.0}>
                Text Size: {textSize}
              </Typography>
              <Tooltip
                title="Adjust the size of text labels in the 3D visualization."
                placement="top"
                arrow
              >
                <HelpOutlineIcon fontSize="small" sx={{ color: 'text.secondary', ml: 0.5, cursor: 'pointer' }} />
              </Tooltip>
            </Box>
            <Slider
              min={1}
              max={24}
              step={1}
              value={textSize}
              onChange={(_, v) => onTextSizeChange(Number(v))}
              valueLabelDisplay="auto"
              sx={{
                width: '95%',
                mb: 1,
                '& .MuiSlider-markLabel': {
                  fontSize: '0.6rem',
                  mt: 0.125,
                },
              }}
              marks={Array.from({ length: 24 - 2 + 1 }, (_, i) => ({
                value: i + 2,
                label: (i + 2).toString(),
              }))}
            />
            {/* Point size slider */}
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <Typography variant="body2" fontWeight={500} mb={0.0}>
                Point Size: {dotSize}
              </Typography>
              <Tooltip
                title="Adjust the size of the points representing each chunk in the visualization."
                placement="top"
                arrow
              >
                <HelpOutlineIcon fontSize="small" sx={{ color: 'text.secondary', ml: 0.5, cursor: 'pointer' }} />
              </Tooltip>
            </Box>
            <Slider
              min={0}
              max={20}
              step={0.5}
              value={dotSize}
              onChange={(_, v) => onDotSizeChange(Number(v))}
              valueLabelDisplay="auto"
              sx={{
                width: '95%',
                mb: 1,
                '& .MuiSlider-markLabel': {
                  fontSize: '0.6rem',
                  mt: 0.125,
                },
              }}
              marks={Array.from({ length: 21 }, (_, i) => ({
                value: i,
                label: i.toString(),
              }))}
            />
            {/* Label offset slider */}
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <Typography variant="body2" fontWeight={500} mb={0.0}>
                Label Offset: {labelOffset}
              </Typography>
              <Tooltip
                title="Adjust the vertical offset of the text labels above each chunk point."
                placement="top"
                arrow
              >
                <HelpOutlineIcon fontSize="small" sx={{ color: 'text.secondary', ml: 0.5, cursor: 'pointer' }} />
              </Tooltip>
            </Box>
            <Slider
              min={0}
              max={1}
              step={0.01}
              value={labelOffset}
              onChange={(_, v) => onLabelOffsetChange(Number(v))}
              valueLabelDisplay="auto"
              sx={{
                width: '95%',
                mb: 1,
                '& .MuiSlider-markLabel': {
                  fontSize: '0.6rem',
                  mt: 0.125,
                },
              }}
              marks={Array.from({ length: 11 }, (_, i) => ({
                value: i * 0.1,
                label: (i * 0.1).toFixed(1),
              }))}
            />
          </Box>
        )}
      </Collapse>
    </Box>
  );
}
